import {useXR, Interactive, useXRInputSourceState, XRHitTest} from "@react-three/xr";
import {useFrame, useThree} from "@react-three/fiber";
import React, {useRef, useState, useEffect, useMemo} from "react";
import * as THREE from "three";
import {Vector3} from "three";
import {objectDirection} from "three/src/nodes/TSL.js";
import {observer_mgl_r3} from "../../../../Utils/ThreeJS/General.js";
import {store} from "../../../../Store/index.js";
import {controllerRight_targetRaySpaceGroup, getControllerPosAndDir} from "../../../../Utils/ThreeJS/XR/Controller.js";
import {Vec2} from "../../../../Utils/ThreeJS/VectorStructs.js";
import {raycaster_main} from "../Utils/Raycasters.js";
import {LHQInstance} from "../LHQ/Scene.js";
import {GetControllerSide_Final} from "../../../../Store/main/settings/vr.js";

export const PaintableSurface = observer_mgl_r3((props: {lhq: LHQInstance, children: React.ReactNode, usePaint: boolean, useVR: boolean, position: THREE.Vector3, rotation: THREE.Euler})=>{
	const {lhq, children, usePaint, useVR, position, rotation} = props;

	const meshRef = useRef<THREE.Mesh | null>(null);
	lhq.paintableSurface_meshRef = meshRef;
	const {gl} = useThree();
	const rightController = useVR ? useXRInputSourceState("controller", GetControllerSide_Final("right")) : null;

	// Handle painting
	let currentFrameHitInfo: HitInfo|n;
	useFrame((state, delta)=>{
		if (!usePaint) return;
		const lastFrameHitInfo = currentFrameHitInfo;
		currentFrameHitInfo = null;

		const rightControllerObj = rightController?.object;
		const rightControllerTarget = rightControllerObj?.position;
		if (rightController == null || rightControllerObj == null || rightControllerTarget == null) return;
		if (!meshRef.current) return;
		const uiState = store.main.tools.vr;
		if (!uiState.isPainting) return;

		const {pos: controllerPos, dir: controllerDir} = getControllerPosAndDir(rightController, GetControllerSide_Final("right"));
		if (controllerPos == null || controllerDir == null) return;

		raycaster_main.set(controllerPos, controllerDir); // Set up and cast the ray
		const directionEndRaycast_hits = raycaster_main.intersectObject(meshRef.current);
		if (directionEndRaycast_hits.length == 0) return;
		//currentFrameHitInfo = new HitInfo({hit: directionEndRaycast_hits[0], canvasPoint: hitCanvasPoint});
		const target_end = directionEndRaycast_hits[0].point;

		// to facilitate line-drawing, we cast rays every 10cm from direction_start (direction from controller to last hit point) to direction_end (direction from controller to current hit point), and draw a circle at each point hit
		// (we cannot simply line-between start and end, as that path may traverse across UV texture seams, which would cause lines spanning across large, unrelated parts of the textures)
		const target_start = lastFrameHitInfo?.hit.point ?? target_end;
		const distance = target_start.distanceTo(target_end);
		const step_initial = 0.1; // 10cm
		// require at least four raycasts (to detect "unusually long uv-distances between hits", ie. detect seams, to not riskily draw lines across them), and at most 10 (to avoid stutter from too many raycasts)
		const steps = Math.floor(distance / step_initial).KeepBetween(6, 10);
		const step_final = distance / steps;
		const startToEndDiff = target_end.clone().sub(target_start);
		const stepVector = startToEndDiff.normalize().multiplyScalar(step_final);

		const rayTargets = [] as THREE.Vector3[];
		for (let i = 0; i < steps; i++) {
			rayTargets.push(target_start.clone().add(stepVector.clone().multiplyScalar(i)));
		}
		rayTargets.push(target_end);
		//console.log(`RayTargets(from:${target_start.toArray()},to:${target_end.toArray()}):`, rayTargets);

		const rayTargetHits = [] as Array<THREE.Intersection>; //[]>;
		const getDirFromPosXToY = (posX: THREE.Vector3, posY: THREE.Vector3)=>new Vector3().subVectors(posY, posX).normalize();
		for (const [i, target] of rayTargets.entries()) {
			raycaster_main.set(controllerPos, getDirFromPosXToY(controllerPos, target)); // Set up and cast the ray
			const hits = raycaster_main.intersectObject(meshRef.current);
			rayTargetHits[i] = hits[0];
		}

		const uvDistancesBetweenRayHits = [] as number[];
		for (let i = 1; i < rayTargetHits.length; i++) {
			const hit = rayTargetHits[i];
			const prevHit = rayTargetHits[i - 1] as THREE.Intersection|null;
			if (prevHit == null || hit == null) continue;
			if (prevHit.uv == null || hit.uv == null) continue;
			const uvDist = prevHit.uv.distanceTo(hit.uv);
			uvDistancesBetweenRayHits.push(uvDist);
		}
		const medianUVDistBetweenRayHits = uvDistancesBetweenRayHits.Median();

		for (let i = 0; i < rayTargets.length; i++) {
			//const rayTarget = rayTargets[i];
			const prevHit = rayTargetHits[i - 1] as THREE.Intersection|null;
			const hit = rayTargetHits[i] as THREE.Intersection|null;
			if (hit == null || !(hit.object instanceof THREE.Mesh)) continue;
			if (hit.uv == null) continue;
			const mat = hit.object.material;
			const texture = mat.map as THREE.CanvasTexture | undefined;
			if (texture == null) continue;
			const canvas = texture.source.data as HTMLCanvasElement; // Get the canvas from the texture
			if (!(canvas instanceof HTMLCanvasElement)) continue; // ignore non-paintable surfaces
			const ctx = canvas.getContext("2d") as CanvasRenderingContext2D; // Get the 2D context
			if (ctx == null) continue;

			ctx.fillStyle = "rgba(0,255,0,1)"; // Paint color (red)

			// Calculate the pixel coordinates from UV
			const getCanvasPointFromUV = (uv: THREE.Vector2)=>{
				//return new Vec2(uv.x * canvas.width, (1 - uv.y) * canvas.height); // Invert Y for canvas coordinates
				return new Vec2(uv.x * canvas.width, uv.y * canvas.height);
			}
			const hitCanvasPoint = getCanvasPointFromUV(hit.uv);
			currentFrameHitInfo = new HitInfo({hit, canvasPoint: hitCanvasPoint});

			// if hit object is same between this ray and last, AND the uv-dist is in the "normal range" (ie. no more than 2x the median), then simply draw a line between the two points
			if (prevHit && prevHit.object == hit.object && hit.uv.distanceTo(prevHit.uv!) <= medianUVDistBetweenRayHits * 2) {
				// Draw a line between the last hit point and the current hit point
				ctx.beginPath();
				const lastRay_canvasPoint = getCanvasPointFromUV(prevHit.uv!);
				ctx.moveTo(lastRay_canvasPoint.x, lastRay_canvasPoint.y);
				ctx.lineTo(hitCanvasPoint.x, hitCanvasPoint.y);
				ctx.strokeStyle = "rgba(0,255,0,1)"; // Paint color (red)
				ctx.lineCap = "round";
				ctx.lineWidth = 5 * 2;
				ctx.stroke();
			} else {
				//if (lastRay_hit?.object != hit.object) console.log("Drawing only dot, because lastRay_hit is not same object", lastRay_hit?.object, hit.object);
				//else if (hit.point.distanceTo(lastRay_hit.point) > step * 2) console.log(`Drawing only dot, because lastRay_hit is too far away ${hit.point.distanceTo(lastRay_hit.point)}. @from:`, lastRay_hit.point, "to:", hit.point);
				// Draw a small circle at the intersection point
				ctx.beginPath();
				ctx.arc(hitCanvasPoint.x, hitCanvasPoint.y, 5, 0, Math.PI * 2); // Draw a circle
				ctx.fill();
			}

			// Update the texture to show the changes
			texture.needsUpdate = true;
		}
	});

	if (!usePaint) return children;
	return (
		<mesh ref={meshRef} position={position} rotation={rotation}>
			{children}
			{/*<meshStandardMaterial
				map={textureRef.current?.texture}
				side={THREE.DoubleSide}
			/>*/}
		</mesh>
	);
});

class HitInfo {
	constructor(data?: Partial<HitInfo>) { Object.assign(this, data); }
	hit: THREE.Intersection;
	canvasPoint: Vec2;
}